import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Opening } from '../lib/contract'
import { useT } from '../lib/i18n'
import { useReveal, usePrefersReducedMotion } from '../lib/useReveal'
import styles from './OpeningRun.module.css'

/**
 * THE OPENING RUN (desk-grammar brief §6.4; four-doors §5.8).
 *
 * The intro is not an intro — it is THE DESK YOU SIT DOWN AT. A short tap-through
 * of the paper the venture itself produced, ending in one diegetic action, then
 * the feed. The player doesn't read an intro; they take over a desk.
 *
 * What that costs this component, in order of how easy each is to get wrong:
 *
 *  - NO SKIP CONTROL. Six taps, not a cutscene (§6.4). A skip button would
 *    concede that the run is something to get past — and it is the opposite: the
 *    cards are the reference material, and they land in the dossier afterwards.
 *  - NO AUTOPLAY. Nothing advances on its own. Reading is untimed because the
 *    paper is the player's now.
 *
 *    ⚠ THE PLAIN CARD'S REVEAL IS THE ONE TIMER, and it is not a breach of that
 *    (2026-08-21). It paces the arrival of words on ONE card; it never advances
 *    to the next, and it never withholds text from a reader who asks — a tap
 *    completes it whole. So nothing is timed except a default, and the default
 *    yields instantly. A reveal that could make someone WAIT would be exactly
 *    the thing this bullet forbids.
 *  - BACK IS ALLOWED. Re-reading is the behaviour the dossier seed rewards.
 *  - THE CLIENT WRITES NO WORD OF IT. Every string on screen is the pack's:
 *    titles, bodies, and the closing action's label. The only text this file
 *    contributes is the aria-labels — machine voice, never on the glass.
 *
 * The one interpretive decision, flagged rather than hidden (dumb-client law 2):
 * bodies carry the pack's own light markup — blank lines between paragraphs and
 * `*emphasis*`, the same convention `documents/` is authored in. Paragraphs and
 * emphasis are rendered as such. Showing literal asterisks on the title card of
 * the game's first impression would read as a defect at exactly the moment
 * authored-feel matters most; interpreting anything richer would be the client
 * making decisions, so nothing richer is interpreted.
 */
export default function OpeningRun({
  opening,
  onComplete,
  actionVariant = 'plate',
}: {
  opening: Opening
  /** The closing action fired. Mount the desk. */
  onComplete: () => void
  /**
   * HOW THE CLOSING CONTROL LOOKS (2026-08-22, stage 1c). `plate` is the desk's bordered
   * control, unchanged; `type` is bare type — the Hildur threshold, whose whole content is the
   * pack's words.
   *
   * ⚠ A NAMED VARIANT RATHER THAN AN INJECTED CLASS, and the first attempt was the latter. Two
   * CSS modules both styling `.action` is a cascade decided by bundler order — measured wrong,
   * survived a doubled selector, and cost an hour. One module owns this control's appearance;
   * callers choose from what it offers.
   */
  actionVariant?: 'plate' | 'type'
}) {
  const t = useT()
  const [index, setIndex] = useState(0)
  const cards = opening.cards
  const card = cards[index]
  const last = index === cards.length - 1

  /*
   * THE PLAIN CARD (pack contract: OpeningCardKind.Plain) — one idea, large, a word at a time.
   *
   * The reveal exists to make FEW WORDS carry weight: a line arriving one word at a time cannot
   * be skimmed, so reading time follows importance rather than length. Deliberately the only
   * kind that gets it — Paper is reference material a reader should be able to scan.
   */
  const plain = card?.kind === 'plain'
  const words = useMemo(() => (card?.body ?? '').split(/\s+/).filter(Boolean).length, [card?.body])
  const reduced = usePrefersReducedMotion()

  // ~150ms a word — a shade slower than reading pace, enough to feel placed rather than typed.
  const revealMs = plain && !reduced ? Math.min(4000, words * 150) : 0

  /*
   * DERIVED DURING RENDER, never set by an effect — and this cost an hour to find.
   *
   * `useReveal` reads `revealMs` on MOUNT ONLY ("mount-only by design", its own comment). An
   * effect that flipped a `revealed` boolean ran one render too late: the card had already
   * mounted carrying the previous card's finished state, the hook captured 0, and the reveal
   * silently never happened. Storing WHICH CARD the reader completed makes the answer correct
   * on the first render of every card, which is the only render the hook looks at.
   */
  const [completedId, setCompletedId] = useState<string | null>(null)
  const revealed = revealMs === 0 || completedId === card?.id

  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])
  const forward = useCallback(
    () => setIndex((i) => Math.min(cards.length - 1, i + 1)),
    [cards.length],
  )

  // The keyboard is a second path to the same two moves, for readers who are not
  // tapping. Enter on the last card is the action itself — the same single
  // control, not a shortcut past it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') back()
      else if (e.key === 'ArrowRight') forward()
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (last) onComplete()
        else forward()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [back, forward, last, onComplete])

  if (!card) return null

  return (
    <div className={styles.run}>
      <article
        className={styles[card.kind] ?? styles.paper}
        // Advancing by tapping the card is the whole gesture; the explicit
        // control below is for readers who need a target, not a second path.
        /*
         * ONE GESTURE, TWO JOBS, in this order: an unfinished reveal completes, and only a
         * finished card advances. A reader faster than the pacing must never be made to wait —
         * and must never lose a card to the very tap that was asking to see it.
         */
        onClick={() => {
          if (!revealed) {
            setCompletedId(card.id)
            return
          }
          if (!last) forward()
        }}
      >
        <div className={styles.sheet}>
          <h1 className={styles.head}>{card.title}</h1>
          {/*
            KEYED ON THE CARD. `useReveal` guards itself with a ref — "runs once per mount, a
            re-render must never restart a reveal" — and React reuses this position across
            cards, so without a key the guard set by card one silently suppresses every reveal
            after it. The key makes each card a real mount, which is what the hook's contract
            assumes. Found by watching a reveal that never moved.
          */}
          <Body key={card.id} text={card.body} revealMs={revealed ? 0 : revealMs} />
        </div>
      </article>

      <nav className={styles.foot} aria-label={t('opening.run')}>
        <button
          className={styles.back}
          onClick={back}
          disabled={index === 0}
          aria-label={t('opening.cardBefore')}
        >
          ‹
        </button>

        <ol className={styles.dots} aria-label={t('opening.cardCount', { n: index + 1, total: cards.length })}>
          {cards.map((c, i) => (
            <li
              key={c.id}
              className={i === index ? styles.dotHere : styles.dot}
              aria-hidden="true"
            />
          ))}
        </ol>

        {last ? (
          /* The run's SINGLE control, in the pack's own words. */
          <button
            className={
              actionVariant === 'type' ? `${styles.action} ${styles.actionType}` : styles.action
            }
            onClick={onComplete}
          >
            {opening.action}
          </button>
        ) : (
          <button className={styles.next} onClick={forward} aria-label={t('opening.nextCard')}>
            ›
          </button>
        )}
      </nav>
    </div>
  )
}

/**
 * One run of text, split into WORDS so the reveal can land them one at a time.
 *
 * Splitting per markup piece was not enough: a body with no emphasis is a single piece, so the
 * whole line appeared at once and the reveal did nothing at all. The unit has to be the word,
 * because the word is what the reader takes in.
 *
 * Whitespace rides with the word that follows it (`/(\S+\s*)/`), so a space never fades on its
 * own and the gaps between words are stable from the first frame.
 */
function worded(run: string, from: number, upto: number, revealMs: number) {
  if (revealMs <= 0) return run
  let at = from
  return run.split(/(\S+\s*)/g).filter(Boolean).map((w, i) => {
    const start = at
    at += w.length
    return (
      <span key={i} className={styles.word} style={{ opacity: start < upto ? 1 : 0 }}>
        {w}
      </span>
    )
  })
}

function Body({ text, revealMs = 0 }: { text: string; revealMs?: number }) {
  /*
   * ⚠ THE REVEAL IS VISUAL PACING, NEVER INFORMATION. The whole text is in the DOM from the
   * first frame — a screen reader reads all of it immediately and the words merely fade in for
   * a reader who is looking. Nothing may ever depend on the animation having run.
   */
  const upto = useReveal(text, revealMs).length
  let cursor = 0
  return (
    <>
      {text.split(/\n{2,}/).map((para, i) => (
        <p key={i} className={styles.para}>
          {para.split(/(\*[^*]+\*)/g).map((piece, j) => {
            const emphasis = piece.startsWith('*') && piece.endsWith('*') && piece.length > 2
            const inner = emphasis ? piece.slice(1, -1) : piece
            // The markup delimiters occupy no visible characters but DO occupy offsets in the
            // source string the reveal counts against, so they are stepped over here.
            if (emphasis) cursor += 1
            const words = worded(inner, cursor, upto, revealMs)
            cursor += inner.length + (emphasis ? 1 : 0)
            return emphasis ? <em key={j}>{words}</em> : <span key={j}>{words}</span>
          })}
        </p>
      ))}
    </>
  )
}
